import { LoadingButton } from '@mui/lab';
import { Box, CircularProgress, FormControl, InputLabel, LinearProgress, ListSubheader, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { checkPermissionOnUpload, getPresets } from '../../../../services/sealing/android';
import { RootState } from '../../../../store';
import { setPermission } from '../../../../store/manage/group';
import { AppURL } from '../../../../utility/AppURL';
import DragnDrop from '../../../utils/dragndrop';
import { showToast } from '../../../utils/notifications';
import { isHybridFramework } from './data';

const acceptFiles = {
    'application/zip': ['.zip'],
    'application/vnd.android.package-archive': ['.apk'],
    'application/x-apk-bundle': ['.aab'],
};

const normFile = (e: any, isHybridFramework: boolean, nativePlan: string | null, hybridPlan: string | null, i81n: any, t: any) => {
    if (Array.isArray(e)) {
        return e;
    }
    let fileSize = 512;
    if (isHybridFramework) {
        if (hybridPlan === 'P') fileSize = 3072;
        else if (hybridPlan === 'E') fileSize = 3072;
    } else {
        if (nativePlan === 'P') fileSize = 3072;
        else if (nativePlan === 'E') fileSize = 3072;
    }

    if (e.file.size / Math.pow(10, 6) > fileSize) {
        showToast(
            'error',
            `${t('the_file_is_too_large')} 
    ${i81n.language === 'kr_KR' ? t('file_size_is_more_than_allocated_configs2') + fileSize + 'MB ' : ''}
    ${t('file_size_is_more_than_allocated_configs')}
    ${i81n.language === 'kr_KR' ? '' : fileSize + 'MB.'}`,
        );
        return [];
    }
    const file = e && e.fileList.length ? [e.fileList[e.fileList.length - 1]] : [];
    return file;
};

interface UploadAosAppProps {
    control: Control;
    formData: any;
    onFieldsChange: any;
    buttonText: string;
    validateInput: () => any;
    securityMeasure: any;
    nativePlanType: string | null;
    hybridPlanType: string | null;
    viewType: string;
    className?: string;
    showGuide?: boolean;
    sendPresets?: any;
    handleSubmit: () => void;
    showLoadMask: any;
}
const UploadAosApp = ({
    control,
    formData,
    handleSubmit,
    onFieldsChange,
    buttonText,
    validateInput,
    securityMeasure,
    nativePlanType,
    hybridPlanType,
    viewType,
    className,
    showGuide,
    sendPresets,
    showLoadMask,
}: UploadAosAppProps) => {
    const { i18n, t } = useTranslation();
    const dispatch = useDispatch();
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [presets, setPresets] = useState<any>(null);
    const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
    const authContext = useSelector((state: RootState) => state.mainReducer.authContext);
    const region = useSelector((state: RootState) => state.mainReducer.region);
    const isBetaAccount = authContext?.is_beta_account;
    const isMember = authContext?.role === 'Member';

    useEffect(() => {
        handleGetPresets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGetPresets = async () => {
        const response = await getPresets(authContext.token, region);
        if (response?.result?.code === '0000') {
            if (isBetaAccount) {
                const mappedPresets = response.data.reduce((acc: any, item: any) => {
                    if (!acc[item.groupName]) {
                        acc[item.groupName] = [];
                    }
                    acc[item.groupName].push(item);
                    return acc;
                }, {});

                setPresets(mappedPresets);
            } else {
                setPresets(response.data);
            }
            setSelectedPresetId(localStorage.getItem('lastUsedPresetId') ?? '');
            localStorage.removeItem('lastUsedPresetId');
        } else {
            showToast('error', t('failed_to_retrieve_preset'));
        }
    };

    useEffect(() => {
        if (showGuide !== undefined && showGuide !== showTooltip) {
            setShowTooltip(showGuide);
        }
    }, [showGuide, showTooltip]);

    const progressColor = () => {
        if (securityMeasure.securityMeasure < 50) {
            return '#E25757';
        } else if (securityMeasure.securityMeasure >= 50 && securityMeasure.securityMeasure < 76) {
            return '#FFA500';
        } else if (securityMeasure.securityMeasure >= 76) {
            return '#127BF5';
        }
    };
    const formatPercent = (percent?: number, successPercent?: number): ReactNode => {
        if (typeof percent === 'number') {
            return viewType === 'step' ? (
                percent + '%'
            ) : (
                <div>
                    <div>{t('security_level')}</div>
                    <div className="text-2xl">{percent}%</div>
                </div>
            );
        }
        return null;
    };

    const handlePresetChange = (event: SelectChangeEvent) => {
        const presetId = event.target.value;
        localStorage.setItem('lastUsedPresetId', presetId);

        if (presetId === '') {
            sendPresets(null);
        } else {
            if (isBetaAccount) {
                const selectedPreset = Object.values(presets)
                    .flat()
                    .find((preset: any) => preset.id === presetId);
                sendPresets(selectedPreset);
            } else {
                const selectedPreset = presets.find((v: any) => v.id === presetId);
                sendPresets(selectedPreset);
            }
        }
    };

    const handleUpload = async (files: any) => {
        if (isMember && isBetaAccount) {
            if (showLoadMask) showLoadMask({ msg: t('validating_user_information') });
            const response = await checkPermissionOnUpload(authContext.token, files);
            const permission = {
                presetActive: response?.data?.presetActive,
                seaLingActive: response?.data?.seaLingActive,
            };
            if (response?.result?.code === '0000') {
                if (permission.presetActive || permission.seaLingActive) {
                    onFieldsChange([{ name: ['apkfile'], value: files }]);
                    dispatch(setPermission(permission));
                } else if (!permission.presetActive && !permission.seaLingActive) {
                    onFieldsChange([{ name: ['apkfile'], value: [] }]);
                    dispatch(
                        setPermission({
                            presetActive: false,
                            seaLingActive: false,
                        }),
                    );
                    showToast('error', t('you_are_not_authorized_to_upload_files'));
                    if (showLoadMask) showLoadMask(false);
                    return false;
                }

                if (showLoadMask) showLoadMask(false);
            } else {
                onFieldsChange([{ name: ['apkfile'], value: [] }]);
                dispatch(
                    setPermission({
                        presetActive: false,
                        seaLingActive: false,
                    }),
                );
                showToast('error', t('you_are_not_authorized_to_upload_files'));
                if (showLoadMask) showLoadMask(false);
                return false;
            }
        } else {
            onFieldsChange([{ name: ['apkfile'], value: files }]);
        }
    };

    return (
        <div className={className}>
            <div className={className ? 'w-full xl:w-1/2' : ''}>
                <Tooltip title={t('please_upload_application_file')} placement="top" arrow open={showTooltip}>
                    <div>
                        <Controller
                            name="apkfile"
                            control={control}
                            defaultValue={formData.apkfile ? formData.apkfile : []}
                            rules={{
                                required: 'Please upload an application file',
                                validate: (files) => files.length > 0 && normFile(files, isHybridFramework(formData.app_framework), nativePlanType, hybridPlanType, i18n, t) !== null,
                            }}
                            render={({ field }) => (
                                <FormControl {...field} className="w-full">
                                    <DragnDrop
                                        color="primary"
                                        appFile={formData.apkfile ? formData.apkfile : []}
                                        onChange={handleUpload}
                                        maxFiles={1}
                                        accept={acceptFiles}
                                        title={t('click_or_drag_application_file')}
                                        subtitle={t('supports_apk_or_appbundle_type_for_application_sealing')}
                                    />
                                </FormControl>
                            )}
                        />
                    </div>
                </Tooltip>
                {presets && formData.apkfile && (
                    <FormControl fullWidth size="small" className="mt-3">
                        <InputLabel id="preset-select-label">{t('preset')}</InputLabel>
                        <Select
                            disabled={!validateInput().basicSetting || (buttonText !== 'START APPSEALING' ? true : false)}
                            size="small"
                            labelId="preset-select-label"
                            id="preset-select"
                            name="sealingPresetId"
                            defaultValue={selectedPresetId ?? ''}
                            label={t('preset')}
                            onChange={handlePresetChange}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxWidth: '300px',
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                    },
                                },
                            }}
                        >
                            <MenuItem value={''} key={'no-preset'} className="text-sm">
                                {t('none')}
                            </MenuItem>
                            {isBetaAccount
                                ? Object.entries(presets).flatMap(([key, v]: [string, any]) => [
                                      <ListSubheader key={`header-${key}`} className="text-xs italic text-[#797A7C] font-semibold">
                                          {key}
                                      </ListSubheader>,
                                      ...v.map((preset: any) => (
                                          <MenuItem key={preset.id} value={preset.id} className="text-sm">
                                              {preset.presetName}
                                          </MenuItem>
                                      )),
                                  ])
                                : presets.map((v: any) => (
                                      <MenuItem value={v.id} key={v.id}>
                                          {v.presetName}
                                      </MenuItem>
                                  ))}
                        </Select>
                    </FormControl>
                )}
                <LoadingButton
                    variant="contained"
                    className="mt-5 text-white text-md"
                    size="large"
                    color="primary"
                    fullWidth
                    onClick={handleSubmit}
                    loading={buttonText !== 'START APPSEALING' ? true : false}
                    disabled={!(validateInput().basicSetting && validateInput().features) || (buttonText !== 'START APPSEALING' ? true : false)}
                >
                    {t(buttonText.toLowerCase().replace(/ /g, '_'), buttonText)}
                </LoadingButton>
            </div>
            <div className={` ${viewType === 'step' ? (className ? 'w-full xl:w-1/2' : '') : 'flex flex-row gap-5 px-2 flex-wrap xl:flex-nowrap'} mt-4`}>
                {validateInput().basicSetting && validateInput().features ? (
                    <React.Fragment>
                        <div>
                            <div>
                                {viewType === 'step' ? (
                                    <div className="mb-3">
                                        <div className="text-sm font-medium">{t('security_level')}</div>
                                        <div className="flex items-center justify-between gap-x-2">
                                            <LinearProgress
                                                variant="determinate"
                                                value={securityMeasure.securityMeasure}
                                                sx={{
                                                    width: '100%',
                                                    height: 10,
                                                    borderRadius: 5,
                                                    '&.MuiLinearProgress-colorPrimary': {
                                                        backgroundColor: '#E9E9E9',
                                                    },
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 5,
                                                        backgroundColor: progressColor() as any,
                                                    },
                                                }}
                                            />
                                            <div className={`text-[${progressColor() as any}] text-xs font-medium`}>{formatPercent(securityMeasure.securityMeasure)}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <Box position="relative" display="inline-flex">
                                        <CircularProgress variant="determinate" size={180} value={securityMeasure.securityMeasure} sx={{ color: progressColor() as any }} />
                                        <div className="absolute text-center transform -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">{formatPercent(securityMeasure.securityMeasure)}</div>
                                    </Box>
                                )}
                            </div>
                        </div>
                        <div className="text-xs">{securityMeasure.securityTip}</div>
                    </React.Fragment>
                ) : (
                    <SecurityGuide />
                )}
            </div>
        </div>
    );
};

const SecurityGuide = () => {
    const {t } = useTranslation();
    return (
        <div className={'overflow-auto flex-grow'} tabIndex={-1}>
            <div>
                <h5 className="font-medium">{t('before_appsealing')}</h5>
                <p className="text-sm">
                    <span>- {t('firebase_crashlytics_should_maintain_their_original_package_name_without_obfuscate_to_be_supported_by_appsealing')}</span>
                    <a className="ml-1 underline cursor-pointer text-linkblue" href={AppURL.beforeSealingLink} target="_blank" rel="noreferrer">
                        {t('read_more')}
                    </a>
                </p>
            </div>
        </div>
    );
};
export default UploadAosApp;
